/**
 * Entities for the "main" module (PromptHub)
 * These entities are shared across presentation, domain hooks, and API layers.
 */

export enum PromptVisibility {
  PRIVATE = "private",
  PUBLIC = "public",
}

export enum MessageRole {
  SYSTEM = "system",
  USER = "user",
  ASSISTANT = "assistant",
}

/**
 * Controlled vocabulary taxonomy types used for publishing.
 * All Tag.name MUST be unique per TagType (enforced server-side).
 */
export enum TagType {
  CATEGORY = "category",
  USE_CASE = "use_case",
  ROLE = "role",
  INDUSTRY = "industry",
}

/**
 * Sorting preference for library listing.
 */
export enum SortOrder {
  UPDATED = "updated",
  CREATED = "created",
}

/**
 * A Tag in the controlled vocabulary managed by Admins.
 */
export interface Tag {
  id: string;
  type: TagType;
  name: string;
  colorHex: string | null; // Optional color hint for UI chips
  createdAt: Date;
  updatedAt: Date | null;
}

/**
 * Enriched representation of a Tag used in Tag Management lists.
 * - slug: auto-generated from Tag.name (lowercase, kebab-case) for display only.
 * - usageCount: number of prompts currently referencing this tag (read only).
 */
export interface TagSummary {
  id: string;
  type: TagType;
  name: string;
  slug: string;
  usageCount: number;
  updatedAt: Date | null;
}

/**
 * A single message within a Prompt template.
 * Content may include dynamic variables like {{variable_name}}.
 */
export interface PromptMessage {
  id: string;
  role: MessageRole;
  content: string;
  createdAt: Date;
  updatedAt: Date | null;
}

/**
 * Association of a Tag with a Prompt. Stored as a flat list for flexibility.
 */
export interface PromptTagAssignment {
  tagId: string;
  type: TagType;
}

/**
 * Represents a Prompt template owned by a user and scoped by organization.
 */
export interface Prompt {
  id: string;
  ownerUserId: string;
  organizationId: string;

  title: string;
  messages: PromptMessage[];

  visibility: PromptVisibility;

  // Tags assigned when published to Explore
  tags: PromptTagAssignment[];

  // Timestamps
  createdAt: Date;
  updatedAt: Date;
  publishedAt: Date | null; // null when private
}

/**
 * Minimal organization info used for UI switcher and context.
 */
export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string | null;
  imageUrl: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

/**
 * Represents a unique public share link generated for a Prompt.
 * The shareId is used to construct a public URL for viewing the prompt.
 * - url: fully-qualified absolute URL that can be copied and shared.
 * - createdAt: when the share link was created.
 */
export interface PromptShareLink {
  promptId: string;
  shareId: string;
  url: string;
  createdAt: Date;
}